import React, { useState, useEffect } from "react";
import {
  CalculatorService,
  type CalculatorLevel,
  type SubmissionResults,
} from "../../services/calculatorService";
import { Button } from "../ui/Button";
import { LockSubmitModal, type LockSubmitModalRef } from "./LockSubmitModal";
import { cx } from "../../utils/functions";
import { HelpIcon } from "../ui/HelpIcon";

interface FormData {
  [rowId: string]: { [columnId: string]: string };
}

interface DryLabAssessmentProps {
  labId: string;
  level: CalculatorLevel;
  initialData: FormData;
  locked: boolean;
  onSubmissionSuccess: (results: SubmissionResults) => void;
  onSubmissionError: (error: string) => void;
}

const WASTE_TYPES = [
  "Single-use coffee cups",
  "Single-use cutlery",
  "Single-use containers",
  "Paper Notebooks",
  "Paper print outs",
  "Printer cartridges",
  "E-waste",
];

const COLUMNS = [
  {
    id: "quantity",
    label: "Quantity (month) *",
    type: "number",
    helpText:
      "An estimate of how much of this waste type your team produces each month.",
  },
  {
    id: "bins_accessible",
    label: "Are bins accessible? *",
    type: "dropdown",
    options: ["Yes", "Partially", "No"],
    helpText:
      "Are the correct bins for this waste type easy for staff to find and use in their daily work areas?",
  },
  {
    id: "bin_liner",
    label: "Bin liner type *",
    type: "dropdown",
    options: [
      "No liner",
      "Plastic (virgin)",
      "Plastic (recycled content)",
      "Compostable",
    ],
    helpText:
      "Indicate if the bin uses a liner and if so what type (recycled or biodegradable).",
  },
  {
    id: "signage_clear",
    label: "Is signage clear? *",
    type: "dropdown",
    options: ["Yes", "Partially", "No"],
    helpText:
      "Are signs present, clear and easy to understand, showing what can and can’t go in this bin?",
  },
  {
    id: "reusable_options",
    label: "Are reusable options in use? *",
    type: "dropdown",
    options: ["Yes", "Limited use", "No"],
    helpText:
      "Is there a reusable alternative in place (for example, reusable cups, containers, or cutlery) to reduce this waste?",
  },
  {
    id: "recycling_program",
    label: "Is a recycling or take-back program in place? *",
    type: "dropdown",
    options: ["Yes", "No"],
    helpText:
      "Is there an active recycling or recovery program for this waste type at your site?",
  },
];

// Helper to check if a cell should be disabled
const isCellDisabled = (rowLabel: string, columnId: string): boolean => {
  if (rowLabel === "Single-use cutlery" && columnId === "quantity") return true;
  if (
    rowLabel === "Single-use containers" &&
    ["quantity", "recycling_program"].includes(columnId)
  )
    return true;
  if (
    rowLabel === "Paper Notebooks" &&
    [
      "quantity",
      "bins_accessible",
      "signage_clear",
      "reusable_options",
      "recycling_program",
    ].includes(columnId)
  )
    return true;
  if (
    rowLabel === "Paper print outs" &&
    [
      "bins_accessible",
      "bin_liner",
      "signage_clear",
      "reusable_options",
      "recycling_program",
    ].includes(columnId)
  )
    return true;
  if (
    rowLabel === "Printer cartridges" &&
    [
      "bins_accessible",
      "bin_liner",
      "signage_clear",
      "reusable_options",
      "recycling_program",
    ].includes(columnId)
  )
    return true;
  if (
    rowLabel === "E-waste" &&
    [
      "bins_accessible",
      "bin_liner",
      "signage_clear",
      "reusable_options",
      "recycling_program",
    ].includes(columnId)
  )
    return true;
  return false;
};

export const DryLabAssessment: React.FC<DryLabAssessmentProps> = ({
  labId,
  level,
  initialData,
  locked,
  onSubmissionSuccess,
  onSubmissionError,
}) => {
  const [formData, setFormData] = useState<FormData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const lockSubmitModalRef = React.useRef<LockSubmitModalRef>(null);

  // Example state for demo/mock as requested
  useEffect(() => {
    if (Object.keys(initialData).length === 0) {
      // Pre-fill with example values if no data exists
      const exampleData: FormData = {
        "Single-use coffee cups": {
          quantity: "10",
          bins_accessible: "yes",
          bin_liner: "No liner",
          signage_clear: "yes",
          reusable_options: "yes",
          recycling_program: "yes",
        },
        "Single-use cutlery": {
          bins_accessible: "no",
          bin_liner: "Plastic (virgin)",
          signage_clear: "no",
          reusable_options: "no",
          recycling_program: "no",
        },
        "Single-use containers": {
          bins_accessible: "partially",
          bin_liner: "Plastic (recycled content)",
          signage_clear: "partially",
          reusable_options: "limited use",
        },
        "Paper Notebooks": {
          bin_liner: "Compostable",
        },
      };
      setFormData(exampleData);
    }
  }, [initialData]);

  useEffect(() => {
    // Simple validation: check if required fields are filled
    // For this assessment, we'll assume any interaction is progress
    setCanSubmit(Object.keys(formData).length > 0);
  }, [formData]);

  const handleFieldChange = (
    rowLabel: string,
    columnId: string,
    value: string,
  ) => {
    if (locked || isCellDisabled(rowLabel, columnId)) return;

    setFormData((prev) => ({
      ...prev,
      [rowLabel]: {
        ...(prev[rowLabel] || {}),
        [columnId]: value,
      },
    }));

    // Auto-save logic would go here, calling CalculatorService.saveFormData
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const response = await CalculatorService.submitCalculator(
        labId,
        "Eco Office",
        level,
      );
      onSubmissionSuccess(response);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to submit assessment";
      onSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 w-full">
      <div className="overflow-x-auto pb-12 -mx-4 px-4 sm:mx-0 sm:px-0">
        <table className="table table-bordered w-full border-collapse bg-white shadow-sm rounded-lg">
          <thead>
            {/* whitespace-normal overrides daisyui's `thead { white-space:
                nowrap }`. Without it every header is pinned to one line, which
                is the only reason this table needed a min-w-[950px] floor. */}
            <tr className="bg-base-200">
              <th className="px-4 py-3 text-left border border-gray-300 whitespace-normal">
                Waste Type
              </th>
              {COLUMNS.map((col, index) => (
                <th
                  key={col.id}
                  className="px-2 py-2 text-left border border-gray-300 min-w-32 whitespace-normal"
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 leading-tight break-words">
                      {col.label}
                    </span>
                    <span
                      className={cx(
                        "tooltip tooltip-custom cursor-help flex items-center z-50",
                        index === COLUMNS.length - 1
                          ? "tooltip-left"
                          : "tooltip-bottom",
                      )}
                      data-tip={col.helpText}
                    >
                      <HelpIcon />
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {WASTE_TYPES.map((rowLabel) => (
              <tr key={rowLabel} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 font-medium border border-gray-300 bg-gray-50 sticky left-0 z-10 text-sm">
                  {rowLabel}
                </td>
                {COLUMNS.map((col) => {
                  const disabled = locked || isCellDisabled(rowLabel, col.id);
                  const value = formData[rowLabel]?.[col.id] || "";

                  return (
                    <td
                      key={col.id}
                      className={cx(
                        "p-1.5 border border-gray-300 align-middle",
                        disabled && "bg-gray-100",
                      )}
                    >
                      {disabled ? (
                        <div className="h-8 w-full"></div>
                      ) : (
                        <>
                          {col.type === "number" && (
                            <input
                              type="number"
                              className="input input-bordered input-sm w-full"
                              value={value}
                              min={0}
                              max={100}
                              placeholder="0"
                              onChange={(e) =>
                                handleFieldChange(
                                  rowLabel,
                                  col.id,
                                  e.target.value,
                                )
                              }
                            />
                          )}
                          {col.type === "dropdown" && (
                            <div className="relative w-full px-2">
                              <select
                                className="appearance-none w-full bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 outline-none focus:border-green-600 focus:ring-2 focus:ring-green-100 transition-all cursor-pointer pr-10"
                                value={value}
                                onChange={(e) =>
                                  handleFieldChange(
                                    rowLabel,
                                    col.id,
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="">Select...</option>
                                {col.options?.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end mt-4">
        <Button
          variant="primary"
          onClick={() => lockSubmitModalRef.current?.showModal()}
          disabled={!canSubmit || isSubmitting || locked}
        >
          {isSubmitting ? (
            <>
              <span className="loading loading-spinner loading-sm mr-2"></span>
              Submitting...
            </>
          ) : (
            "Submit Assessment"
          )}
        </Button>
      </div>

      <LockSubmitModal
        ref={lockSubmitModalRef}
        onConfirm={handleSubmit}
        loading={isSubmitting}
      />
    </div>
  );
};
